import type { LadderTemplate } from '../types';

export const futsalShowLadder: LadderTemplate = {
  id: 'futsal-show-ladder',
  name: 'Futsal Show Ladder',
  requiredStandings: 7,
  matches: [
    // Round 1: Barrages
    {
      id: 'barrage1',
      label: 'Barrage 1',
      round: 1,
      player1: { from: 'standings', position: 6 },
      player2: { from: 'standings', position: 7 },
    },
    {
      id: 'barrage2',
      label: 'Barrage 2',
      round: 1,
      player1: { from: 'standings', position: 4 },
      player2: { from: 'standings', position: 5 },
    },
    // Round 2: Barrage 3 + Duel Show
    {
      id: 'barrage3',
      label: 'Barrage 3',
      round: 2,
      player1: { from: 'barrage1', outcome: 'winner' },
      player2: { from: 'standings', position: 3 },
    },
    {
      id: 'duel-show',
      label: 'Duel Show',
      round: 2,
      player1: { from: 'barrage1', outcome: 'loser' },
      player2: { from: 'barrage2', outcome: 'loser' },
    },
    // Round 3: Semi-finals
    {
      id: 'demi1',
      label: 'Demi 1',
      round: 3,
      player1: { from: 'barrage2', outcome: 'winner' },
      player2: { from: 'standings', position: 2 },
    },
    {
      id: 'demi2',
      label: 'Demi 2',
      round: 3,
      player1: { from: 'standings', position: 1 },
      player2: { from: 'barrage3', outcome: 'winner' },
    },
    // Round 4: Final
    {
      id: 'final',
      label: 'Final',
      round: 4,
      player1: { from: 'demi1', outcome: 'winner' },
      player2: { from: 'demi2', outcome: 'winner' },
    },
  ],
};

export const futsalShowLadder9: LadderTemplate = {
  id: 'futsal-show-ladder-9',
  name: 'Futsal Show Ladder (9 teams)',
  requiredStandings: 9,
  matches: [
    // Round 1: Play-in (8th vs 9th)
    {
      id: 'm-0',
      label: 'Play-in',
      round: 1,
      player1: { from: 'standings', position: 8 },
      player2: { from: 'standings', position: 9 },
    },
    // Round 2: Quarter-finals
    {
      id: 'm-1',
      label: 'Quarter 1',
      round: 2,
      player1: { from: 'standings', position: 1 },
      player2: { from: 'm-0', outcome: 'winner' },
    },
    {
      id: 'm-2',
      label: 'Quarter 2',
      round: 2,
      player1: { from: 'standings', position: 3 },
      player2: { from: 'standings', position: 4 },
    },
    {
      id: 'm-3',
      label: 'Quarter 3',
      round: 2,
      player1: { from: 'standings', position: 2 },
      player2: { from: 'standings', position: 5 },
    },
    {
      id: 'm-4',
      label: 'Quarter 4',
      round: 2,
      player1: { from: 'standings', position: 6 },
      player2: { from: 'standings', position: 7 },
    },
    // Round 3: Semi-finals
    {
      id: 'm-1-2',
      label: 'Semi 1',
      round: 3,
      player1: { from: 'm-1', outcome: 'winner' },
      player2: { from: 'm-2', outcome: 'winner' },
    },
    {
      id: 'm-3-4',
      label: 'Semi 2',
      round: 3,
      player1: { from: 'm-3', outcome: 'winner' },
      player2: { from: 'm-4', outcome: 'winner' },
    },
    // Round 4: Final
    {
      id: 'final',
      label: 'Final',
      round: 4,
      player1: { from: 'm-1-2', outcome: 'winner' },
      player2: { from: 'm-3-4', outcome: 'winner' },
    },
  ],
};

/** Registry of all available ladder templates */
export const ladderTemplates: Record<string, LadderTemplate> = {
  'futsal-show-ladder': futsalShowLadder,
  'futsal-show-ladder-9': futsalShowLadder9,
};

export function getTemplate(id: string): LadderTemplate | undefined {
  return ladderTemplates[id];
}

export function validateTemplate(template: LadderTemplate): { valid: boolean; errors: string[] } {
  const errors: string[] = [];
  const matchIds = new Set(template.matches.map((m) => m.id));
  const matchRounds = new Map(template.matches.map((m) => [m.id, m.round]));

  for (const match of template.matches) {
    if (template.matches.filter((m) => m.id === match.id).length > 1) {
      errors.push(`Duplicate match ID: ${match.id}`);
    }
    for (const slot of [match.player1, match.player2]) {
      if (slot.from === 'bye') {
        continue; // a bye slot has no opponent; nothing to validate
      }
      if (slot.from === 'standings') {
        if (!slot.position || slot.position < 1) {
          errors.push(`${match.id}: standings slot missing valid position`);
        }
      } else {
        if (!matchIds.has(slot.from)) {
          errors.push(`${match.id}: references unknown match '${slot.from}'`);
        }
        const sourceRound = matchRounds.get(slot.from);
        if (sourceRound !== undefined && sourceRound >= match.round) {
          errors.push(
            `${match.id}: references '${slot.from}' from round ${sourceRound} (must be < ${match.round})`,
          );
        }
        if (!slot.outcome) {
          errors.push(`${match.id}: match reference missing outcome`);
        }
      }
    }
  }
  return { valid: errors.length === 0, errors };
}

/**
 * Selects the Futsal Show ladder bracket for a given enrolled team count.
 * 7 → the live 7-team ladder; 9 → the 9-team ladder. Throws for any other
 * count so the draw form surfaces a clear "7 or 9 teams only" error.
 */
export function resolveFutsalShowLadder(teamCount: number): LadderTemplate {
  switch (teamCount) {
    case 7:
      return futsalShowLadder;
    case 9:
      return futsalShowLadder9;
    default:
      throw new Error(`Futsal Show ladder supports 7 or 9 teams only, got ${teamCount}`);
  }
}
